import type { BinaryLabel, Horizon, Preset, Timeframe } from "../types";
import type { Setup, SetupCandle, SetupPattern, SetupStructure } from "./setup";

/**
 * The measured base rates behind the status bar's "always Higher/Green"
 * benchmark (GRA-151), plus the copy that names a setup in the reveal line.
 *
 * The pipeline aggregates one entry per (loop, timeframe, preset, label) out of
 * the round bank it just built and writes `public/data/setup-stats.json`; the
 * runtime fetches that file lazily, once, and looks one entry up for the
 * status bar. This module owns the shape, its validation, the fetch cache,
 * and the copy, everything except the aggregation itself, which is
 * build-time only and lives in `dataPipeline/buildSetupStats.ts`.
 *
 * The file is a few kB against banks of tens of MB, so it is fetched on its
 * own rather than folded into a bank, and a missing or malformed file simply
 * means no benchmark: the status bar falls back to the random-guess baseline.
 */

/** One measured cell: how many Rounds carried the label, and how often Higher. */
export type SetupStat = {
  /** Rounds counted. */
  n: number;
  /** Share of them that resolved Higher, 0..1. */
  higherShare: number;
};

/** The three label families measured for one (loop, timeframe, preset) bucket. */
export type SetupBucket = {
  structure: Partial<Record<SetupStructure, SetupStat>>;
  candle: Partial<Record<SetupCandle, SetupStat>>;
  pattern: Partial<Record<SetupPattern, SetupStat>>;
};

/**
 * The whole stats file. `trend` is keyed by timeframe then preset, since a
 * trend Round resolves differently at each difficulty; `nextCandle` is keyed by
 * timeframe alone, because a candle is green or red regardless of preset.
 * Everything is partial: only the (timeframe, horizon) pairs that have a bank
 * appear, so trend carries daily only.
 *
 * `majorityDirection` (GRA-151, version 2) is the same shape minus the
 * structure/candle keys: one whole-population `SetupStat` per bucket, "always
 * guess Higher/Green"'s actual score at that (timeframe, preset). It backs the
 * status bar's benchmark beside the random-guess baseline, so it is measured
 * over exactly the same population `trend`/`nextCandle` count from: a trend
 * bucket excludes time-cap rounds, matching what the trend Loop actually
 * serves (`barrierPreset` in `src/lib/rounds.ts`).
 */
export type SetupStats = {
  version: 3;
  trend: Partial<Record<Timeframe, Partial<Record<Preset, SetupBucket>>>>;
  nextCandle: Partial<Record<Timeframe, SetupBucket>>;
  majorityDirection: {
    trend: Partial<Record<Timeframe, Partial<Record<Preset, SetupStat>>>>;
    nextCandle: Partial<Record<Timeframe, SetupStat>>;
  };
};

/** Below this many Rounds the measured share is noise, so it is not shown. */
export const MIN_SAMPLE = 50;

/**
 * How each structure reads in the reveal line. Every phrase names the
 * *broader* shape of the whole Context, the thing a local formation sits
 * inside rather than argues with, which is why `mixed` reads "no single
 * broader structure": a Context can match no structure rule and still draw a
 * pattern, and the old "no clear structure with a bear flag" read as a
 * contradiction.
 */
export const STRUCTURE_COPY: Record<SetupStructure, string> = {
  uptrend: "in an uptrend",
  downtrend: "in a downtrend",
  range: "in a tight range",
  breakout_up: "breakout above a range",
  breakout_down: "breakdown below a range",
  mixed: "no single broader structure",
};

/**
 * How each candle label reads, as the tail of "last candle …". Named
 * descriptively rather than with the trading-floor "bullish/bearish
 * engulfing": the line only describes what the Context drew, and a
 * directional adjective in it would read as the advice this feature
 * deliberately does not give.
 */
export const CANDLE_COPY: Record<SetupCandle, string> = {
  doji: "a doji",
  hammer: "a hammer",
  shooting_star: "a shooting star",
  bullish_engulfing: "an engulfing up bar",
  bearish_engulfing: "an engulfing down bar",
  inside_bar: "an inside bar",
};

/**
 * How each multi-candle pattern reads, inside the line's own local-formation
 * clause ("; a bear flag in the recent candles"). Plain names with no
 * article, so the line can pick "a" or "an" for itself, and no directional
 * gloss for the same reason the candle copy has none.
 */
export const PATTERN_COPY: Record<SetupPattern, string> = {
  head_and_shoulders: "head and shoulders",
  inverse_head_and_shoulders: "inverse head and shoulders",
  double_top: "double top",
  double_bottom: "double bottom",
  bull_flag: "bull flag",
  bear_flag: "bear flag",
  rising_wedge: "rising wedge",
  falling_wedge: "falling wedge",
};

/** "a" or "an" for `word`, which is all the copy above ever needs. */
function article(word: string): string {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

// ---------------------------------------------------------------------------
// The line itself.
// ---------------------------------------------------------------------------

/**
 * One piece of the setup sentence. `term` marks a segment that names a
 * candle or pattern label, the two pieces of the sentence `SetupLine`
 * (the React rendering) links out to a glossary entry; a segment with no
 * `term` is connective tissue (the structure clause, "; a", " in the recent
 * candles", "last candle a", …) and stays plain text everywhere.
 */
export type SetupLineSegment = {
  text: string;
  term?: "candle" | "pattern";
};

/**
 * The setup sentence's pieces, or null when the Round carries no setup (a
 * bank built before this feature). Both `setupLine` (the plain string, used
 * by tests and share text) and `SetupLine` (the React rendering, which links
 * the candle and pattern terms) build on this so the two can never drift
 * apart: joining every segment's `text` reproduces `setupLine` exactly.
 *
 * A Round reads as, for example,
 *
 * `Setup: breakout above a range`
 *
 * A Round that also draws a classic formation names it between the structure
 * and the last candle, in a clause of its own, as
 *
 * `Setup: in a tight range; a double top in the recent candles, last candle a doji`
 *
 * The clause is separate on purpose. The structure describes the whole
 * Context and the pattern is a local formation inside it, so no combination
 * of the two can read as a contradiction:
 *
 * `Setup: no single broader structure; a bear flag in the recent candles`
 *
 * Wording is a description of the Context and nothing more: no "expect", no
 * direction to take, and nothing about what the formation will do next.
 */
export function setupLineSegments(setup: Setup | undefined): SetupLineSegment[] | null {
  if (!setup) return null;
  const structure = STRUCTURE_COPY[setup.structure];
  // A bank written by a newer detector could carry a label this build has no
  // copy for. Saying nothing beats rendering "Setup: undefined".
  if (!structure) return null;

  const segments: SetupLineSegment[] = [{ text: `Setup: ${structure}` }];

  const patternCopy = setup.pattern ? PATTERN_COPY[setup.pattern] : undefined;
  if (patternCopy) {
    // Its own clause, and explicitly local: the structure above reads the
    // whole Context, this reads a formation sitting inside it.
    segments.push(
      { text: `; ${article(patternCopy)} ` },
      { text: patternCopy, term: "pattern" },
      { text: " in the recent candles" },
    );
  }

  const candleCopy = setup.candle ? CANDLE_COPY[setup.candle] : undefined;
  if (candleCopy) {
    // CANDLE_COPY bakes its own article in ("a doji", "an inside bar"), unlike
    // PATTERN_COPY, so it is split back into article + bare term here rather
    // than duplicating a second bare-copy table.
    const [, candleArticle, candleTerm] = /^(a|an) (.+)$/.exec(candleCopy) ?? [
      "",
      "",
      candleCopy,
    ];
    segments.push(
      { text: `, last candle ${candleArticle} ` },
      { text: candleTerm, term: "candle" },
    );
  }

  return segments;
}

/**
 * The reveal's setup sentence as plain text, or null when the Round carries
 * no setup. See `setupLineSegments` for the sentence's construction; this is
 * just its segments joined.
 */
export function setupLine(setup: Setup | undefined): string | null {
  const segments = setupLineSegments(setup);
  return segments ? segments.map((s) => s.text).join("") : null;
}

/** Which bucket a Loop reads: its horizon, timeframe and (for trend) preset. */
export type SetupStatKey = {
  horizon: Horizon;
  timeframe: Timeframe;
  preset: Preset;
};

/**
 * The whole-population "always guess Higher/Green" cell for a Loop, the
 * status bar's benchmark beside the random-guess baseline (GRA-151).
 */
export function lookupMajorityDirection(
  stats: SetupStats | null,
  key: SetupStatKey,
): SetupStat | undefined {
  if (!stats) return undefined;
  return key.horizon === "trend"
    ? stats.majorityDirection.trend[key.timeframe]?.[key.preset]
    : stats.majorityDirection.nextCandle[key.timeframe];
}

/**
 * The status bar's rendered "always X" benchmark: a label and a percent.
 *
 * Both are a count over the Rounds this bank holds, never a chance attached
 * to the next Round: "always Higher scored 54% of these Rounds" is a fact
 * about the bank, and any copy built on it has to read that way.
 */
export type MajorityDirection = {
  /** "Higher"/"Lower" for a trend Loop, "Green"/"Red" for a next-candle one. */
  label: string;
  /** Whichever share is the majority, so this is always ≥ 50. */
  percent: number;
};

const MAJORITY_LABEL: Record<Horizon, Record<BinaryLabel, string>> = {
  trend: { up: "Higher", down: "Lower" },
  nextCandle: { up: "Green", down: "Red" },
};

/**
 * Reduce a whole-population stat to the majority call and its share, e.g.
 * "always Higher" at 54% rather than "always Higher" at the raw (and
 * possibly minority) `higherShare`. Dropped below `MIN_SAMPLE`, same as the
 * setup line's own stat clause.
 */
export function majorityDirection(
  stat: SetupStat | undefined,
  horizon: Horizon,
): MajorityDirection | null {
  if (!stat || stat.n < MIN_SAMPLE) return null;
  const isHigher = stat.higherShare >= 0.5;
  const percent = Math.round((isHigher ? stat.higherShare : 1 - stat.higherShare) * 100);
  return { label: MAJORITY_LABEL[horizon][isHigher ? "up" : "down"], percent };
}

// ---------------------------------------------------------------------------
// Loading.
// ---------------------------------------------------------------------------

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

function isStat(x: unknown): x is SetupStat {
  return (
    isRecord(x) &&
    typeof x.n === "number" &&
    Number.isFinite(x.n) &&
    typeof x.higherShare === "number" &&
    Number.isFinite(x.higherShare)
  );
}

function isBucket(x: unknown): x is SetupBucket {
  if (!isRecord(x) || !isRecord(x.structure) || !isRecord(x.candle)) return false;
  if (!isRecord(x.pattern)) return false;
  return (
    Object.values(x.structure).every(isStat) &&
    Object.values(x.candle).every(isStat) &&
    Object.values(x.pattern).every(isStat)
  );
}

/**
 * Validate a parsed stats file, returning null for anything malformed. The
 * caller treats null as "no stat clause", so a bad file degrades to the
 * description alone rather than throwing at reveal.
 */
export function loadSetupStats(raw: unknown): SetupStats | null {
  if (!isRecord(raw) || raw.version !== 3) return null;
  if (!isRecord(raw.trend) || !isRecord(raw.nextCandle)) return null;
  if (!isRecord(raw.majorityDirection)) return null;
  const { trend: majorityTrend, nextCandle: majorityNextCandle } = raw.majorityDirection;
  if (!isRecord(majorityTrend) || !isRecord(majorityNextCandle)) return null;

  for (const byPreset of Object.values(raw.trend)) {
    if (!isRecord(byPreset)) return null;
    if (!Object.values(byPreset).every(isBucket)) return null;
  }
  if (!Object.values(raw.nextCandle).every(isBucket)) return null;

  for (const byPreset of Object.values(majorityTrend)) {
    if (!isRecord(byPreset)) return null;
    if (!Object.values(byPreset).every(isStat)) return null;
  }
  if (!Object.values(majorityNextCandle).every(isStat)) return null;

  return raw as unknown as SetupStats;
}

type FetchLike = (url: string) => Promise<{ json: () => Promise<unknown> }>;

/**
 * Fetch and validate `setup-stats.json`. Never rejects: a network error, a 404
 * on a bank generated before this feature, or a malformed body all resolve to
 * null.
 */
export async function fetchSetupStats(
  baseUrl = "/data",
  fetchFn: FetchLike = fetch,
): Promise<SetupStats | null> {
  try {
    const raw = await (await fetchFn(`${baseUrl}/setup-stats.json`)).json();
    return loadSetupStats(raw);
  } catch {
    return null;
  }
}

const cache = new Map<string, Promise<SetupStats | null>>();

/**
 * The stats file, fetched at most once per `baseUrl` per session and shared by
 * every Loop's reveal. Lazy on purpose: nothing needs it until the first
 * commit, and it must not compete with the multi-MB bank load at startup.
 */
export function sharedSetupStats(
  baseUrl = "/data",
  fetchFn: FetchLike = fetch,
): Promise<SetupStats | null> {
  const cached = cache.get(baseUrl);
  if (cached) return cached;
  const pending = fetchSetupStats(baseUrl, fetchFn);
  cache.set(baseUrl, pending);
  return pending;
}

/** Forget the cached stats (test isolation). */
export function resetSharedSetupStats(): void {
  cache.clear();
}
